import { useParams, Link } from "react-router-dom";
import { categories, comics } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === category?.name);

  const jsonLd = category
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "http://uhomekj.com/" },
              { "@type": "ListItem", position: 2, name: `${category.name}漫画`, item: `http://uhomekj.com/category/${slug}` }
            ]
          },
          {
            "@type": "CollectionPage",
            name: `${category.name}漫画 - 韩漫大全漫画网`,
            description: category.description,
            url: `http://uhomekj.com/category/${slug}`,
            isPartOf: { "@type": "WebSite", name: "韩漫大全漫画网", url: "http://uhomekj.com/" }
          }
        ]
      }
    : undefined;

  usePageMeta({
    title: category ? `${category.name}漫画_韩漫大全漫画网下拉式免费无删减在线` : "韩漫大全漫画网",
    description: category ? `韩漫大全漫画网${category.name}分类，${category.description}，共收录${category.count}部${category.name}韩漫，全部免费下拉式阅读。` : "韩漫大全漫画网",
    keywords: category ? `韩漫大全漫画网,${category.name}韩漫,${category.name}漫画,下拉式免费,无删减韩漫` : "韩漫大全漫画网",
    canonical: `http://uhomekj.com/category/${slug}`,
    jsonLd,
  });

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20manga%20library%20background%2C%20warm%20atmospheric%20gradient%20from%20soft%20peach%20to%20cream%20beige%20tones%2C%20abstract%20flowing%20geometric%20patterns%2C%20minimalist%20artistic%20illustration%20style%2C%20dreamy%20soft%20focus%2C%20delicate%20paper%20texture%2C%20warm%20golden%20light%2C%20no%20text%20no%20people&width=1920&height=800&seq=category-hero-03&orientation=landscape"
            alt={`${category.name}漫画分类`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>

        <div className="relative z-10 h-full w-full px-4 md:px-6 flex flex-col justify-center">
          <div className="max-w-3xl">
            <h1 className="font-heading text-2xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
              {category.name}漫画
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-4 max-w-lg leading-relaxed">
              {category.description}
            </p>
          </div>
        </div>
      </section>

      <main className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: `${category.name}漫画` }]} />
          </div>

          <div className="mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-950">{categoryComics.length}</strong> 部漫画
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {categoryComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group block bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                data-product-shop=""
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <span className="text-white text-xs font-medium">
                      更新至第{comic.latestChapter}话
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h2 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors mb-1 line-clamp-1">
                    {comic.title}
                  </h2>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{comic.author}</span>
                    <span className="text-xs text-primary-500 font-medium">{comic.rating}分</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {comic.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-accent-100 text-accent-900 text-xs rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryComics.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无漫画</p>
              <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">返回首页</Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}